import {
  AlertLogsApiGetFiltersRequest,
  AlertLogsApiGetGroupTicketsRequest,
  AlertLogsApiGetTicketsRequest,
  AlertLogsApiSyncAllTicketsRequest,
  AlertLogsApiSyncTicketRequest,
  BulkTicketSyncResponse,
  FilterResponse,
  PaginatedGroupTicketsResponse,
  PaginatedTicketsResponse,
  TicketsResponse,
  TicketSyncResponse,
} from '@ariksa/notification/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { each } from 'lodash';
import { QueryAction } from 'services/types';
import { PagedQueryState } from 'services/utils/PagedQueryState';
import { QueryState } from 'services/utils/QueryState';

import { PageInfo } from 'components/Navigation';
import { createSlice } from 'utils/@reduxjs/toolkit';

import { ContainerState, ChildTicketsRequest } from './types';

// The initial state of the Alerts container
export const initialState: ContainerState = {
  tickets: PagedQueryState.init([]),
  childTickets: PagedQueryState.init([]),
  syncTicketInformation: QueryState.init({} as TicketSyncResponse),
  syncAllTicketsInformation: QueryState.init({} as BulkTicketSyncResponse),
  ticketStatus: [],
  assignees: QueryState.init([]),
  issueAssignees: QueryState.init([]),
};

const ticketSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    getTickets(
      state,
      action: QueryAction<
        PaginatedTicketsResponse,
        AlertLogsApiGetTicketsRequest
      >,
    ) {
      state.tickets = PagedQueryState.next(state.tickets, action, {
        mapData: r => {
          state.ticketStatus = r.ticket_states ?? [];
          return r.tickets;
        },
        mapTotalCount: r => r.total,
      });
    },
    updateTicketPagination(state, action: PayloadAction<PageInfo>) {
      state.tickets.page.info = action.payload;
    },
    getGroupTickets(
      state,
      action: QueryAction<
        PaginatedGroupTicketsResponse,
        AlertLogsApiGetGroupTicketsRequest
      >,
    ) {
      state.tickets = PagedQueryState.next(state.tickets, action, {
        mapData: r => {
          return r.tickets;
        },
        mapTotalCount: r => r.total,
      });
    },

    getAssignees(
      state,
      action: QueryAction<FilterResponse, AlertLogsApiGetFiltersRequest>,
    ) {
      state.assignees = QueryState.next(state.assignees, action, {
        mapData: r => r?.assignees ?? [],
      });
    },

    getIssueAssignees(
      state,
      action: QueryAction<FilterResponse, AlertLogsApiGetFiltersRequest>,
    ) {
      state.issueAssignees = QueryState.next(state.issueAssignees, action, {
        mapData: r => r?.assignees ?? [],
      });
    },

    getChildTickets(
      state,
      action: QueryAction<PaginatedTicketsResponse, ChildTicketsRequest>,
    ) {
      state.childTickets = PagedQueryState.next(state.childTickets, action, {
        mapData: r => {
          return r.tickets;
        },
        mapTotalCount: r => r.total,
      });
    },
    updateChildTicketPagination(state, action: PayloadAction<PageInfo>) {
      state.childTickets.page.info = action.payload;
    },

    syncTicketInformation(
      state,
      action: QueryAction<TicketSyncResponse, AlertLogsApiSyncTicketRequest>,
    ) {
      state.syncTicketInformation = QueryState.next(
        state.syncTicketInformation,
        action,
        {
          mapData: res => {
            let items: TicketsResponse[] = [];
            each(state.tickets.data, o => {
              if (res?.ticket_id === o.ticket_id)
                items.push({
                  ...o,
                  last_sync_time: res?.ticket_last_sync ?? o?.last_sync_time,
                  due_date: res.ticket_due_date,
                  state: res?.status,
                });
              else items.push(o);
            });
            state.tickets.data = items;
            return res;
          },
        },
      );
    },

    syncAllTicketsInformation(
      state,
      action: QueryAction<
        BulkTicketSyncResponse,
        AlertLogsApiSyncAllTicketsRequest
      >,
    ) {
      state.syncAllTicketsInformation = QueryState.next(
        state.syncAllTicketsInformation,
        action,
        {
          mapData: res => {
            let items: TicketsResponse[] = [];
            let syncedTickets: Record<string, TicketSyncResponse> = {};
            each(res.synced_tickets, o => (syncedTickets[o.ticket_id] = o));
            each(state.tickets.data, o => {
              const data = syncedTickets[o.ticket_id];
              if (!!data)
                items.push({
                  ...o,
                  last_sync_time: data?.ticket_last_sync ?? o?.last_sync_time,
                  due_date: data.ticket_due_date,
                  state: data?.status,
                });
              else items.push(o);
            });
            state.tickets.data = items;
            return res;
          },
        },
      );
    },
  },
});

export const { actions, reducer, name: sliceKey } = ticketSlice;
